export const ROLE_KEYS = [
  "amministratore",
  "tecnico_lab",
  "clinic_owner",
  "dentista",
  "aso",
  "staff",
] as const;

export type AppRole = (typeof ROLE_KEYS)[number];

export const ROLE_LABELS: Record<AppRole, string> = {
  amministratore: "Amministratore",
  tecnico_lab: "Tecnico laboratorio",
  clinic_owner: "Titolare clinica",
  dentista: "Dentista",
  aso: "ASO",
  staff: "Personale clinica",
};

export const ROLE_DESCRIPTIONS: Record<AppRole, string> = {
  amministratore: "Controllo completo della piattaforma e delle configurazioni protette.",
  tecnico_lab: "Operatività trasversale del laboratorio sulle società clienti.",
  clinic_owner: "Gestione clinica, listini, prescrizioni e dati economici delle società collegate.",
  dentista: "Attività cliniche e prescrizioni personali nelle società collegate.",
  aso: "Supporto operativo alle prescrizioni e alle attività della clinica.",
  staff: "Attività amministrative e contabili autorizzate della clinica.",
};

export const CLINIC_ROLE_KEYS: readonly AppRole[] = [
  "clinic_owner",
  "dentista",
  "aso",
  "staff",
];

export const PERMISSION_CATEGORIES = [
  { key: "workspace", label: "Accesso e organizzazione" },
  { key: "prescriptions", label: "Prescrizioni" },
  { key: "catalog", label: "Listini e lavorazioni" },
  { key: "accounting", label: "Contabilità e prezzi" },
  { key: "componentistica", label: "Componentistica" },
  { key: "quality", label: "Qualità e condivisione" },
  { key: "system", label: "Sistema e integrazioni" },
] as const;

export type PermissionCategory = (typeof PERMISSION_CATEGORIES)[number]["key"];

export type PermissionDefinition = {
  key: string;
  label: string;
  description: string;
  category: PermissionCategory;
  defaultRoles: readonly AppRole[];
  /** Se presente, il permesso non può essere assegnato agli altri ruoli. */
  allowedRoles?: readonly AppRole[];
  protected?: boolean;
};

/**
 * Versione del catalogo salvata assieme alle policy personalizzate.
 *
 * Le policy create prima di questo campo sono alla versione 0: il resolver
 * applica soltanto gli additive defaults introdotti successivamente, senza
 * trasformare una futura disabilitazione esplicita in un permesso riattivato.
 */
export const CURRENT_PERMISSION_CATALOG_VERSION = 4;

const ALL_ROLES: readonly AppRole[] = ROLE_KEYS;
const LAB_ROLES: readonly AppRole[] = ["amministratore", "tecnico_lab"];
const EXTERNAL_WORK_ROLES: readonly AppRole[] = ["amministratore", "clinic_owner", "dentista", "aso", "staff"];
const EXTERNAL_MANAGEMENT_ROLES: readonly AppRole[] = ["amministratore", "clinic_owner", "staff"];
const CLINIC_AND_LAB: readonly AppRole[] = [
  "amministratore",
  "tecnico_lab",
  "clinic_owner",
  "dentista",
  "aso",
];

export const PERMISSION_CATALOG: readonly PermissionDefinition[] = [
  {
    key: "view_componentistica",
    label: "Consultazione componentistica",
    description: "Consulta giacenze e scarichi ed esporta Excel nelle società abilitate, rispettando le cliniche assegnate.",
    category: "componentistica",
    defaultRoles: ALL_ROLES,
  },
  {
    key: "view_organization_componentistica",
    label: "Scarichi di tutta la società",
    description: "Estende la consultazione degli scarichi a tutte le cliniche abilitate della società autorizzata.",
    category: "componentistica",
    defaultRoles: ["amministratore"],
  },
  {
    key: "manage_componentistica",
    label: "Configurazione componentistica",
    description: "Abilita società e magazzini, associa le cliniche sorgente e gestisce le credenziali delle integrazioni.",
    category: "componentistica",
    defaultRoles: ["amministratore"],
    allowedRoles: ["amministratore"],
    protected: true,
  },
  {
    key: "manage_external_laboratories",
    label: "Laboratori e listini esterni",
    description: "Gestisce laboratori e listini esterni delle società autorizzate, scegliendo le cliniche abilitate all'uso.",
    category: "catalog",
    defaultRoles: EXTERNAL_MANAGEMENT_ROLES,
    allowedRoles: EXTERNAL_MANAGEMENT_ROLES,
  },
  {
    key: "view_external_prescriptions",
    label: "Prescrizioni esterne",
    description: "Consulta i lavori con laboratori esterni autorizzati nella clinica attiva.",
    category: "prescriptions",
    defaultRoles: EXTERNAL_WORK_ROLES,
    allowedRoles: EXTERNAL_WORK_ROLES,
  },
  {
    key: "create_external_prescription",
    label: "Gestione prescrizioni esterne",
    description: "Crea e aggiorna prescrizioni locali per i laboratori esterni della clinica attiva.",
    category: "prescriptions",
    defaultRoles: EXTERNAL_WORK_ROLES,
    allowedRoles: EXTERNAL_WORK_ROLES,
  },
  {
    key: "view_external_prices",
    label: "Costi laboratori esterni",
    description: "Consulta i costi dei cataloghi esterni autorizzati e dei lavori della clinica attiva.",
    category: "accounting",
    defaultRoles: EXTERNAL_MANAGEMENT_ROLES,
    allowedRoles: EXTERNAL_MANAGEMENT_ROLES,
  },
  {
    key: "view_dashboard",
    label: "Dashboard",
    description: "Visualizza la pagina iniziale e i riepiloghi del contesto attivo.",
    category: "workspace",
    defaultRoles: ALL_ROLES,
  },
  {
    key: "view_company_data",
    label: "Dati della clinica",
    description: "Consulta dati e riferimenti delle società a cui l’utente è collegato.",
    category: "workspace",
    defaultRoles: ALL_ROLES,
  },
  {
    key: "view_companies",
    label: "Elenco società e cliniche",
    description: "Accede all’elenco e alle schede delle società autorizzate.",
    category: "workspace",
    defaultRoles: ["amministratore", "tecnico_lab", "clinic_owner", "dentista"],
  },
  {
    key: "view_company_users",
    label: "Utenti della clinica",
    description: "Consulta i professionisti necessari ai flussi clinici della società attiva.",
    category: "workspace",
    defaultRoles: ["amministratore", "tecnico_lab", "clinic_owner", "aso"],
  },
  {
    key: "manage_users",
    label: "Gestione utenti",
    description: "Modifica ruoli, società collegate e stato degli utenti.",
    category: "workspace",
    defaultRoles: ["amministratore"],
  },
  {
    key: "manage_companies",
    label: "Gestione società e cliniche",
    description: "Gestisce società, cliniche, dati fiscali e collegamenti PrimoUp.",
    category: "workspace",
    defaultRoles: LAB_ROLES,
  },
  {
    key: "manage_company_branding",
    label: "Brand della clinica",
    description: "Personalizza colore e logo della società attiva.",
    category: "workspace",
    defaultRoles: ["amministratore", "tecnico_lab", "clinic_owner"],
    allowedRoles: ["amministratore", "tecnico_lab", "clinic_owner"],
  },
  {
    key: "view_prescriptions",
    label: "Consultazione prescrizioni",
    description: "Apre elenco e dettaglio delle prescrizioni autorizzate.",
    category: "prescriptions",
    defaultRoles: CLINIC_AND_LAB,
  },
  {
    key: "view_all_prescriptions",
    label: "Prescrizioni di tutte le cliniche",
    description: "Consulta trasversalmente tutte le prescrizioni del laboratorio.",
    category: "prescriptions",
    defaultRoles: LAB_ROLES,
  },
  {
    key: "view_company_prescriptions",
    label: "Prescrizioni della clinica",
    description: "Consulta tutte le prescrizioni della società attiva, non solo le proprie.",
    category: "prescriptions",
    defaultRoles: ["amministratore", "tecnico_lab", "clinic_owner", "aso"],
  },
  {
    key: "create_prescription",
    label: "Nuove prescrizioni",
    description: "Crea e invia nuove prescrizioni di laboratorio.",
    category: "prescriptions",
    defaultRoles: CLINIC_AND_LAB,
  },
  {
    key: "manage_prescriptions",
    label: "Gestione avanzata prescrizioni",
    description: "Modifica, ripara e gestisce prescrizioni per conto del laboratorio.",
    category: "prescriptions",
    defaultRoles: LAB_ROLES,
  },
  {
    key: "use_prescription_chat",
    label: "Chat prescrizioni",
    description: "Utilizza la comunicazione contestuale collegata alle prescrizioni.",
    category: "prescriptions",
    defaultRoles: CLINIC_AND_LAB,
  },
  {
    key: "view_listino",
    label: "Listino della clinica",
    description: "Consulta il listino operativo e le configurazioni della clinica.",
    category: "catalog",
    defaultRoles: ["amministratore", "tecnico_lab", "clinic_owner"],
  },
  {
    key: "view_listino_medico",
    label: "Listino medico",
    description: "Consulta il listino destinato al professionista.",
    category: "catalog",
    defaultRoles: ["amministratore", "clinic_owner", "dentista"],
  },
  {
    key: "manage_listino",
    label: "Gestione listini",
    description: "Crea, modifica e archivia le voci di listino.",
    category: "catalog",
    defaultRoles: LAB_ROLES,
  },
  {
    key: "request_catalog_listino",
    label: "Richieste catalogo",
    description: "Invia al laboratorio richieste di nuove voci di catalogo.",
    category: "catalog",
    defaultRoles: ["clinic_owner"],
  },
  {
    key: "approve_catalog_listino",
    label: "Approvazione catalogo",
    description: "Valuta e risolve le richieste di catalogo delle cliniche.",
    category: "catalog",
    defaultRoles: LAB_ROLES,
  },
  {
    key: "manage_company_phases",
    label: "Fasi della società",
    description: "Configura le fasi di lavorazione disponibili per società e prestazione.",
    category: "catalog",
    defaultRoles: ["amministratore", "tecnico_lab", "clinic_owner"],
  },
  {
    key: "manage_phase_policies",
    label: "Policy di salto fase",
    description: "Gestisce regole e approvazioni per i salti di fase.",
    category: "catalog",
    defaultRoles: LAB_ROLES,
    allowedRoles: LAB_ROLES,
  },
  {
    key: "manage_implant_config",
    label: "Configurazione impianti",
    description: "Gestisce il catalogo e le regole degli impianti.",
    category: "catalog",
    defaultRoles: LAB_ROLES,
    allowedRoles: LAB_ROLES,
  },
  {
    key: "view_accounting",
    label: "Contabilità",
    description: "Consulta contabilità, cedolini e movimenti della società attiva.",
    category: "accounting",
    defaultRoles: ["amministratore", "tecnico_lab", "clinic_owner", "staff"],
  },
  {
    key: "view_analytics",
    label: "Analytics laboratorio",
    description: "Consulta aggregazioni cross-clinic per cliente, periodo e tipologia.",
    category: "accounting",
    defaultRoles: LAB_ROLES,
    allowedRoles: LAB_ROLES,
  },
  {
    key: "view_prices",
    label: "Prezzi",
    description: "Visualizza prezzi e importi economici nelle aree autorizzate.",
    category: "accounting",
    defaultRoles: ["amministratore", "tecnico_lab", "clinic_owner"],
  },
  {
    key: "export_accounting_csv",
    label: "Esportazione contabilità",
    description: "Esporta i movimenti contabili della società autorizzata; gli importi richiedono il permesso Prezzi.",
    category: "accounting",
    defaultRoles: ["amministratore", "tecnico_lab", "clinic_owner", "staff"],
  },
  {
    key: "export_accounting_csv_global",
    label: "Esportazione contabilità globale",
    description: "Esporta dati contabili trasversali a tutte le società; gli importi richiedono il permesso Prezzi.",
    category: "accounting",
    defaultRoles: LAB_ROLES,
  },
  {
    key: "manage_contabilita_esclusioni",
    label: "Code contabilità",
    description: "Gestisce esclusioni e anomalie nelle code contabili.",
    category: "accounting",
    defaultRoles: LAB_ROLES,
  },
  {
    key: "segnala_contabilita_problema",
    label: "Segnalazioni contabili",
    description: "Segnala un problema contabile collegato a una prescrizione.",
    category: "accounting",
    defaultRoles: CLINIC_AND_LAB,
  },
  {
    key: "view_iso_reports",
    label: "Segnalazioni ISO",
    description: "Consulta le segnalazioni qualità ISO autorizzate.",
    category: "quality",
    defaultRoles: CLINIC_AND_LAB,
  },
  {
    key: "manage_iso_reports",
    label: "Gestione segnalazioni ISO",
    description: "Prende in carico e gestisce le segnalazioni qualità.",
    category: "quality",
    defaultRoles: LAB_ROLES,
  },
  {
    key: "view_customer_health",
    label: "Performance",
    description: "Consulta volumi, feedback e indicatori economici autorizzati.",
    category: "quality",
    defaultRoles: ["tecnico_lab"],
  },
  {
    key: "view_shared_files",
    label: "File condivisi",
    description: "Accede all’area dei file condivisi per chirurgia guidata.",
    category: "quality",
    defaultRoles: ALL_ROLES,
  },
  {
    key: "manage_settings",
    label: "Impostazioni operative",
    description: "Gestisce notifiche e impostazioni operative della piattaforma.",
    category: "system",
    defaultRoles: LAB_ROLES,
  },
  {
    key: "manage_primoup_integration",
    label: "Integrazione PrimoUp",
    description: "Configura e verifica il collegamento tecnico con PrimoUp.",
    category: "system",
    defaultRoles: ["amministratore"],
    allowedRoles: ["amministratore"],
    protected: true,
  },
  {
    key: "manage_communication_templates",
    label: "Comunicazioni",
    description: "Configura modelli e regole delle comunicazioni automatiche.",
    category: "system",
    defaultRoles: LAB_ROLES,
  },
  {
    key: "use_api_keys",
    label: "Utilizzo API key",
    description: "Genera e utilizza le chiavi API previste per il proprio ruolo.",
    category: "system",
    defaultRoles: ["amministratore", "tecnico_lab", "clinic_owner"],
  },
  {
    key: "manage_api_keys",
    label: "Gestione API key",
    description: "Amministra e revoca le chiavi API degli utenti.",
    category: "system",
    defaultRoles: LAB_ROLES,
  },
  {
    key: "view_system_health",
    label: "Stato del sistema",
    description: "Consulta indicatori tecnici e stato dei servizi.",
    category: "system",
    defaultRoles: LAB_ROLES,
  },
  {
    key: "use_impersonation",
    label: "Impersonation amministrativa",
    description: "Opera temporaneamente con il profilo di un altro utente.",
    category: "system",
    defaultRoles: ["amministratore"],
    protected: true,
  },
  {
    key: "manage_iso_webhook",
    label: "Webhook ISO",
    description: "Gestisce credenziali e contratto del webhook ISO.",
    category: "system",
    defaultRoles: ["amministratore"],
    protected: true,
  },
  {
    key: "manage_role_permissions",
    label: "Ruoli e permessi",
    description: "Configura le autorizzazioni globali assegnate ai ruoli.",
    category: "system",
    defaultRoles: ["amministratore"],
    protected: true,
  },
] as const;

export const PERMISSION_KEYS = PERMISSION_CATALOG.map((permission) => permission.key);
export const PROTECTED_PERMISSION_KEYS = PERMISSION_CATALOG
  .filter((permission) => permission.protected)
  .map((permission) => permission.key);

export function isAppRole(value: string): value is AppRole {
  return (ROLE_KEYS as readonly string[]).includes(value);
}

export function isClinicRole(value: string): value is AppRole {
  return (CLINIC_ROLE_KEYS as readonly string[]).includes(value);
}

export function isKnownPermission(value: string): boolean {
  return PERMISSION_KEYS.includes(value);
}

export function isPermissionAllowedForRole(
  permissionKey: string,
  role: AppRole
): boolean {
  const permission = PERMISSION_CATALOG.find(
    (definition) => definition.key === permissionKey
  );
  return Boolean(
    permission &&
      (!permission.allowedRoles || permission.allowedRoles.includes(role))
  );
}

export function getDefaultPermissionsForRole(role: AppRole): string[] {
  if (role === "amministratore") return [...PERMISSION_KEYS];
  return PERMISSION_CATALOG
    .filter(
      (permission) =>
        permission.defaultRoles.includes(role) &&
        isPermissionAllowedForRole(permission.key, role)
    )
    .map((permission) => permission.key);
}

export type RolePermissionPolicySnapshot = {
  enabled_permissions: readonly string[];
  catalog_version?: number;
};

/**
 * Risolve una policy senza scrivere dati, così le righe legacy restano sicure
 * anche prima di un'eventuale migrazione persistente.
 */
export function resolvePermissionsForRole(
  role: AppRole,
  policy?: RolePermissionPolicySnapshot | null
): string[] {
  if (role === "amministratore") return getDefaultPermissionsForRole(role);

  const resolved = policy
    ? policy.enabled_permissions.filter(
        (permission) =>
          isKnownPermission(permission) &&
          !PROTECTED_PERMISSION_KEYS.includes(permission) &&
          isPermissionAllowedForRole(permission, role)
      )
    : getDefaultPermissionsForRole(role);

  const catalogVersion = policy?.catalog_version ?? 0;
  if (policy && catalogVersion < 1 && (role === "clinic_owner" || role === "tecnico_lab")) {
    resolved.push("manage_company_branding");
  }
  if (policy && catalogVersion < 2 && role === "tecnico_lab") {
    resolved.push("view_analytics");
  }
  if (policy && catalogVersion < 3) {
    for (const permission of PERMISSION_CATALOG) {
      if (["manage_external_laboratories", "view_external_prescriptions", "create_external_prescription", "view_external_prices"].includes(permission.key) &&
          permission.defaultRoles.includes(role) && isPermissionAllowedForRole(permission.key, role)) {
        resolved.push(permission.key);
      }
    }
  }
  if (policy && catalogVersion < 4) {
    // La consultazione resta subordinata all'abilitazione della società;
    // nessun accesso societario esteso viene introdotto automaticamente.
    resolved.push("view_componentistica");
  }

  return Array.from(new Set(resolved));
}

export function getRoleLabel(role: string): string {
  return isAppRole(role) ? ROLE_LABELS[role] : role;
}
